'use client';

import { useTranslation } from 'next-i18next/pages';
import { FiCheckCircle } from 'react-icons/fi';

import type { CardProps } from '@highlight-cp/core-ui';
import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Loader,
  Stack,
  Tooltip,
} from '@highlight-cp/core-ui';

import classes from './product-card.module.css';

export type ProductCardProps = CardProps & {
  label?: string;
  isSaving?: boolean;
  isValid?: boolean;
  onApplyAll?: () => void;
};

/**
 * ### ProductCard
 *
 * @description
 * A `Card` for the product-setup screens: an optional translated label badge, a status slot showing
 * a saving spinner, a "ready" check or a "missing required fields" dot, and an optional
 * "Apply to All" action below the content.
 *
 * @example
 * <ProductCard label="Product 1" isValid={isValid} isSaving={isSaving} onApplyAll={applyToAll}>
 *   {fields}
 * </ProductCard>
 *
 * @param props - {@link ProductCardProps}
 * @returns React component
 */
export const ProductCard = ({
  children,
  isSaving,
  isValid,
  label,
  onApplyAll,
  ...rest
}: ProductCardProps) => {
  const { t } = useTranslation();

  return (
    <Card {...rest}>
      <Stack gap="sm">
        {label && (
          <>
            <Flex align="center" justify="space-between" gap="xs" w="100%">
              <Box miw={0}>
                <Tooltip label={label}>
                  <Badge color="blue">{t(label)}</Badge>
                </Tooltip>
              </Box>
              <Flex align="center" gap="xs">
                {/* role="img" so the aria-label is actually exposed: a bare <svg> and a <div>
                    both map to a role that carries no accessible name of its own. */}
                {!isSaving && isValid && (
                  <FiCheckCircle
                    aria-label={t('Product Ready')}
                    color="var(--mantine-color-success)"
                    role="img"
                    size="20"
                    title={t('Product Ready')}
                  />
                )}
                {!isSaving && !isValid && (
                  <Tooltip label={t('Missing required fields')} position="top">
                    <Box
                      aria-label={t('Missing required fields')}
                      className={classes.invalidCircle}
                      role="img"
                    />
                  </Tooltip>
                )}
                {isSaving && <Loader size={16} />}
              </Flex>
            </Flex>
            <Divider />
          </>
        )}
        {children}
        {onApplyAll && (
          <>
            <Divider />
            {/* Tooltip needs a real element to anchor to; the Button itself is disabled-able */}
            <Tooltip label={t('Apply these settings to all products')}>
              <Box w="100%">
                <Button variant="light" onClick={onApplyAll} size="xs" w="100%">
                  {t('Apply to All')}
                </Button>
              </Box>
            </Tooltip>
          </>
        )}
      </Stack>
    </Card>
  );
};
